import React, { useRef, useCallback } from "react"
import { UploadTask, useUpload } from "@/contexts/UploadContext"
import { Progress } from "@/components/ui/progress"
import { Button } from "@/components/ui/button"
import {
	X,
	AlertCircle,
	CheckCircle2,
	Loader2,
	RotateCcw,
	FileWarning,
	Folder,
} from "lucide-react"
import { formatFileSize } from "@/utils/formatFileSize"
import { cn } from "@/lib/utils"
import { uploadService } from "@/services/uploadService"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip"

/**
 * Props for the {@link UploadItem} component.
 */
interface UploadItemProps {
	/** The upload task to display. */
	task: UploadTask
}

/**
 * Renders an individual item in the upload queue.
 * Displays file name, size, progress, status, and actions (cancel, retry, resume).
 *
 * @component
 * @param props - The props for the component.
 * @param props.task - The upload task object to render.
 * @returns A React element representing an item in the upload queue.
 */
const UploadItem: React.FC<UploadItemProps> = ({ task }) => {
	const { dispatchUploadAction } = useUpload()
	const fileInputRef = useRef<HTMLInputElement>(null)

	const handleCancel = async () => {
		if (
			task.status === "uploading" ||
			task.status === "pending" ||
			task.status === "paused" ||
			task.status === "pending_resume"
		) {
			await uploadService.abortUpload(task, dispatchUploadAction)
		}
		dispatchUploadAction({ type: "REMOVE_TASK", payload: { id: task.id } })
	}

	const handleRetry = () => {
		if (task.file) {
			dispatchUploadAction({
				type: "UPDATE_TASK_STATUS",
				payload: { id: task.id, status: "pending", error: undefined },
			})
		} else if (task.sessionUri) {
			dispatchUploadAction({
				type: "UPDATE_TASK_STATUS",
				payload: {
					id: task.id,
					status: "pending_resume",
					error: "Ready to retry. Please re-select file if prompted.",
				},
			})
		} else {
			dispatchUploadAction({
				type: "UPDATE_TASK_STATUS",
				payload: {
					id: task.id,
					status: "error",
					error: "Cannot retry: File and session info missing.",
				},
			})
		}
	}

	const handleResumeFileSelect = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		if (event.target.files && event.target.files[0]) {
			const file = event.target.files[0]
			// Basic validation: could be more robust (e.g., check type, lastModified if available)
			if (file.name === task.fileName && file.size === task.fileSize) {
				dispatchUploadAction({
					type: "SET_FILE_FOR_RESUME",
					payload: { id: task.id, file },
				})
			} else {
				dispatchUploadAction({
					type: "UPDATE_TASK_STATUS",
					payload: {
						id: task.id,
						status: "error",
						error: "Selected file does not match original.",
					},
				})
			}
		}
		// Reset file input
		if (fileInputRef.current) {
			fileInputRef.current.value = ""
		}
	}

	const triggerResumeFileSelect = () => {
		fileInputRef.current?.click()
	}

	const getStatusIcon = () => {
		switch (task.status) {
			case "uploading":
				return (
					<Loader2 className="h-5 w-5 flex-shrink-0 text-blue-500 animate-spin" />
				)
			case "completed":
				return <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-500" />
			case "error":
				return <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
			case "pending_resume":
				return <FileWarning className="h-5 w-5 flex-shrink-0 text-yellow-500" />
			case "pending":
			case "paused": // Paused might need a different icon later
				return (
					<Loader2 className="h-5 w-5 flex-shrink-0 text-muted-foreground animate-spin opacity-50" />
				)
			default:
				return null
		}
	}

	return (
		<div className="bg-card border rounded-lg p-3 space-y-2 shadow-sm">
			<input
				type="file"
				ref={fileInputRef}
				onChange={handleResumeFileSelect}
				className="hidden"
				accept={task.fileType || "*/*"}
			/>
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-3 flex-1 min-w-0">
					{getStatusIcon()}
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-1">
							{task.relativePath && (
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger>
											<Folder className="h-3 w-3 text-muted-foreground" />
										</TooltipTrigger>
										<TooltipContent>
											<p>Path: {task.relativePath || task.fileName}</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							)}
							<p
								className="text-sm font-medium truncate text-foreground"
								title={task.relativePath || task.fileName}
							>
								{task.relativePath ? task.relativePath.split('/').pop() : task.fileName}
							</p>
						</div>
						<p className="text-xs text-muted-foreground">
							{formatFileSize(task.fileSize)}
							{task.status === "error" && task.error && (
								<span className="text-red-600 ml-1">- {task.error}</span>
							)}
							{task.status === "pending_resume" && (
								<span className="text-yellow-600 ml-1">
									- Awaiting file re-selection to resume.
								</span>
							)}
						</p>
					</div>
				</div>
				<Button
					variant="ghost"
					size="icon"
					className="h-7 w-7 rounded-full flex-shrink-0"
					onClick={handleCancel}
					aria-label="Cancel upload"
				>
					<X className="h-4 w-4" />
				</Button>
			</div>

			{(task.status === "uploading" ||
				(task.status === "pending_resume" && task.progress > 0)) && (
				<Progress value={task.progress} className={cn("h-1.5 w-full")} />
			)}

			{(task.status === "error" || task.status === "pending_resume") && (
				<div className="flex items-center justify-end space-x-2 pt-1">
					{task.status === "error" && (
						<Button
							variant="outline"
							size="sm"
							onClick={handleRetry}
							className="text-xs"
						>
							<RotateCcw className="h-3 w-3 mr-1.5" />
							Retry
						</Button>
					)}
					{task.status === "pending_resume" && (
						<Button
							variant="outline"
							size="sm"
							onClick={triggerResumeFileSelect}
							className="text-xs"
						>
							<FileWarning className="h-3 w-3 mr-1.5" />
							Select File to Resume
						</Button>
					)}
				</div>
			)}
		</div>
	)
}

export default UploadItem
